import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Grid, makeStyles, MenuItem } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { choixResidenceEnum, espaceExterieurEnum, formulaireChangementAppartementEnum, nombreChambreEnum, nombrePiecesEnum, ouiNonEnum, profilLocataireCRMEnum, typeVehiculeEnum, villeEnum } from '../enums/formulaireChangementAppartementEnum'
import { Field, Form } from 'react-final-form'
import { FormulaireChangementAppartementModel } from '../types/formulaireChangementAppartementModel'
import { objectGetKeys } from '../../../utils/ieCompatibility'
import { FieldArray } from 'react-final-form-arrays'
import SelecteurVille from './SelecteurVille'
import arrayMutators from 'final-form-arrays'
import FormFabField from '../../../components/fields/FormFabField'
import FormNumberFormatField from '../../../components/fields/FormNumberFormatField'
import { formTypeEnum } from '../../../components/fields/enum/formTypeEnum'
import Tooltip from '../../../components/tooltip/Tooltip'
import InfoIcon from '@material-ui/icons/Info'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import styles from '../../../styles'
import classnames from 'classnames'
import { getSpecificDate } from '../../../utils/getDays/getNextDate'
import { WHITE } from '../../../theme'
import FormDatePickerAcquiResiField from '../../../components/fields/FormDatePickerAcquiResiField'
import FormTextFieldInline from '../../../components/fields/FormTextFieldInline'
import RaisonRecherche from './RaisonRecherche'
import { typeProspectEnum } from '../../profile/enums/typeProspectEnum'
import ProfilLocataire from './ProfilLocataire'
import { AppContext } from '../../../containers/context/AppContext'
import CheckBoxField from '../../../components/fields/CheckBoxField'
import FormNumberWithButtonsField from '../../../components/fields/FormNumberWithButtonsField'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import Loader from '../../../components/loader/Loader'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import Button from '../../../components/button/Button'
import { ClassValue } from 'classnames/types'
import { Theme } from '@material-ui/core/styles/createMuiTheme'
import { ResponsiveContext } from '../../../utils/context/responsiveContext'
import linksEnum from '../../../enums/linksEnum'
import { useHistory } from 'react-router-dom'
import { unflatten } from 'flat'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../reducers'
import { contractsPhaseEnum } from '../../../enums/contractsEnum'
import { ContractsModel } from '../../../types/contracts/contractsModel'
import ROLE from '../../../enums/interlocuteurRoleEnum'
import SelecteurCopreneur from './SelecteurCopreneur'
import { useLocation } from 'react-router'
import { POST_CHANGEMENT_APPARTEMENT, postChangementAppartement } from '../api/formulaireChangementAppartementAPI'
import errorCatcher from '../../../utils/errorCatcher'
import { typeCandidatureEnum } from '../enums/changementAppartementEnum'
import { redirectionVersAcquisitionResidence } from '../utils/changementAppartementUtils'
import DimensionVehicule from './DimensionVehicule'
import possedeDossierValide from '../../../utils/possedeDossierValide'
import PromoCode from './PromoCode'

const useStyles = makeStyles<Theme, { isLargeScreen: boolean }>(
	(theme) => (
		{
			...styles(theme),
			titleStyle: {
				...styles(theme).title6,
				fontSize: 28,
				margin: '5px 0 15px 0',
				color: theme.palette.blue.main
			},
			conteneurChamp: {
				marginTop: props => props.isLargeScreen ? 30 : 40
			},
			tooltipContainerStyle: {
				right: 15
			},
			devise: {
				marginLeft: 10
			},
			labelStyle: {
				...styles(theme).text5,
				color: theme.palette.blue.main,
				marginBottom: props => props.isLargeScreen ? 15 : 25,
				marginTop: props => props.isLargeScreen ? 0 : 40,
				position: 'relative'
			},
			datepicker: {
				backgroundColor: WHITE,
				padding: 10
			},
			buttonLongText: {
				width: props => props.isLargeScreen ? 250 : '100%',
				position: 'relative',
				padding: '15px 6px',

				'& > span:first-child': {
					marginRight: props => props.isLargeScreen ? 25 : 0
				}
			},
			chevronRightStyle: {
				position: props => props.isLargeScreen ? 'absolute' : 'inherit',
				right: 15
			},
			widthInherit: {
				position: 'relative',
				width: 'inherit'
			},
			btnContinue: {
				textAlign: 'right',
				paddingRight: 8,
				position: 'relative',
				width: 'inherit'
			},
			bottomButtons: {
				marginTop: 30,

				'& > div:first-child': {
					marginTop: props => props.isLargeScreen ? 0 : 16
				}
			},
			flexColumnReverse: {
				flexDirection: 'column-reverse' as 'column-reverse'
			},
			greenText: {
				textTransform: 'lowercase',
				color: theme.palette.green.main,
				fontWeight: 'bold'
			}
		}
	)
)

interface StateModel {
	typeDemande?: typeCandidatureEnum
}

export const SPACE: string = 'space'

const FormulaireChangementAppartement = () => {
	const { userInfos } = useContext(AppContext)
	const { isLargeScreen } = useContext(ResponsiveContext)
	const classes = useStyles({ isLargeScreen })
	const intl = useIntl()
	const history = useHistory()
	const location = useLocation<StateModel | undefined>()
	const dossier = useSelector((state: RootState) => state.dossier)
	const contracts = useSelector((state: RootState) => state.contracts)
	const dispatch = useDispatch()
	const [open, setOpen] = useState(false)
	const [openDateArrivee, setOpenDateArrivee] = useState(false)
	const [postingData, setPostingData] = useState(false)

	useEffect(
		() => {
			if (possedeDossierValide(dossier) || (!location.state || (location.state && !location.state.typeDemande))) {
				history.push(linksEnum.requests)
			}

			return () => location.state = undefined
		}, [history, location.state, dossier]
	)

	const formValeursInitiales = useMemo(
		() => (
			{
				[formulaireChangementAppartementEnum.choixResidence]: choixResidenceEnum.ouiPreference,
				[formulaireChangementAppartementEnum.ville]: [],
				[formulaireChangementAppartementEnum.nombrePieces]: [],
				[formulaireChangementAppartementEnum.espaceExterieur]: [],
				[formulaireChangementAppartementEnum.profilLocataire]: dossier?.eft_profil_utilisateur,
				[formulaireChangementAppartementEnum.nbColoc]: 2,
				[formulaireChangementAppartementEnum.isStudent]: ouiNonEnum.non,
				[formulaireChangementAppartementEnum.isBoursier]: ouiNonEnum.non
			}
		), [dossier]
	)

	const dateText = classnames(
		classes.text8,
		classes.colorBlue,
		classes.semiBold
	)

	const buttonContainerClass: ClassValue = classnames(
		classes.bottomButtons,
		{ [classes.flexColumnReverse]: !isLargeScreen }
	)

	const buttonWidthClass: ClassValue = classnames(
		classes.btnContinue,
		classes.widthInherit
	)

	const buttonClass: ClassValue = classnames(
		classes.buttonLongText
	)

	// Vérification de la présence de colocataire(s) dans les interlocuteurs du contrat
	const contractAvecColocataire = (contract: ContractsModel) => contract.interlocuteursRoleBail.filter(
		(interlocuteurRoleBail) => interlocuteurRoleBail.eft_role === ROLE.COLOCATAIRE
	)

	// Récupération des contrats en exploitation et avec des colocataires (l'utilisateur connecté n'étant pas le seul colocataire)
	const contratExploitationEtColocataire = () => (contracts as ContractsModel[]).filter((contract) => contract.phase === contractsPhaseEnum.exploitation && contractAvecColocataire(contract).length > 0)

	const concerneAppartement = () => location.state?.typeDemande === typeCandidatureEnum.ajoutAppartement || location.state?.typeDemande === typeCandidatureEnum.changementAppartement

	const concerneAjoutParking = () => location.state?.typeDemande === typeCandidatureEnum.ajoutParking

	const validate = (valeurs: FormulaireChangementAppartementModel) => {
		const erreurs: any = {}

		// On effectue des checks s'il s'agit d'une demande pour un appartement
		if (concerneAppartement()) {
			if (valeurs[formulaireChangementAppartementEnum.nombrePieces].length === 0) {
				erreurs[formulaireChangementAppartementEnum.nombrePieces] = intl.formatMessage({
					id: 'global.errors.required',
					defaultMessage: 'Requis*',
					description: 'Required message'
				})
			}
			if (!valeurs[formulaireChangementAppartementEnum.nombreChambres]) {
				erreurs[formulaireChangementAppartementEnum.nombreChambres] = intl.formatMessage({
					id: 'global.errors.required',
					defaultMessage: 'Requis*',
					description: 'Required message'
				})
			}
			if (!valeurs[formulaireChangementAppartementEnum.budgetMaximum]) {
				erreurs[formulaireChangementAppartementEnum.budgetMaximum] = intl.formatMessage({
					id: 'global.errors.required',
					defaultMessage: 'Requis*',
					description: 'Required message'
				})
			}
			if (!valeurs[formulaireChangementAppartementEnum.raisonRecherche]) {
				erreurs[formulaireChangementAppartementEnum.raisonRecherche] = {
					[formulaireChangementAppartementEnum.raisonRechercheReponse]: intl.formatMessage({
						id: 'global.errors.required',
						defaultMessage: 'Requis*',
						description: 'Required message'
					})
				}
			}
		}
		// Checks s'il s'agit d'une demande pour un parking
		else {
			if (!valeurs[formulaireChangementAppartementEnum.typeVehicule]) {
				erreurs[formulaireChangementAppartementEnum.typeVehicule] = intl.formatMessage({
					id: 'global.errors.required',
					defaultMessage: 'Requis*',
					description: 'Required message'
				})
			} else if (valeurs[formulaireChangementAppartementEnum.typeVehicule] === typeVehiculeEnum.voiture) {
				if (!valeurs[formulaireChangementAppartementEnum.longueur] || valeurs[formulaireChangementAppartementEnum.longueur]!.toString().length === 0) {
					erreurs[formulaireChangementAppartementEnum.longueur] = intl.formatMessage({
						id: 'global.errors.required',
						defaultMessage: 'Requis*',
						description: 'Required message'
					})
				}

				if (!valeurs[formulaireChangementAppartementEnum.largeur] || valeurs[formulaireChangementAppartementEnum.largeur]!.toString().length === 0) {
					erreurs[formulaireChangementAppartementEnum.largeur] = intl.formatMessage({
						id: 'global.errors.required',
						defaultMessage: 'Requis*',
						description: 'Required message'
					})
				}

				if (!valeurs[formulaireChangementAppartementEnum.hauteur] || valeurs[formulaireChangementAppartementEnum.hauteur]!.toString().length === 0) {
					erreurs[formulaireChangementAppartementEnum.hauteur] = intl.formatMessage({
						id: 'global.errors.required',
						defaultMessage: 'Requis*',
						description: 'Required message'
					})
				}
			}
		}

		return unflatten<typeof erreurs, FormulaireChangementAppartementModel>(erreurs)
	}

	const onSubmit = (valeurs: FormulaireChangementAppartementModel) => {
		setPostingData(true)
		switch (valeurs[formulaireChangementAppartementEnum.profilLocataire]) {
			case profilLocataireCRMEnum.alone:
				valeurs[formulaireChangementAppartementEnum.nbCopreneurs] = null
				break
			case profilLocataireCRMEnum.couple:
				valeurs[formulaireChangementAppartementEnum.nbCopreneurs] = 1
				break
			case profilLocataireCRMEnum.family:
				valeurs[formulaireChangementAppartementEnum.nbCopreneurs] = valeurs[formulaireChangementAppartementEnum.familleMonoparental] ? null : 1
				break
			case profilLocataireCRMEnum.other:
				valeurs[formulaireChangementAppartementEnum.nbCopreneurs] = valeurs[formulaireChangementAppartementEnum.nbColoc] !== null ? (valeurs[formulaireChangementAppartementEnum.nbColoc] as number) - 1 : 1
				break
			default:
				valeurs[formulaireChangementAppartementEnum.nbCopreneurs] = null
				break
		}

		if (valeurs[formulaireChangementAppartementEnum.choixResidence] !== choixResidenceEnum.non) {
			valeurs[formulaireChangementAppartementEnum.ville] = []
		}
		postChangementAppartement(JSON.stringify({
			...valeurs,
			typeDemande: Number(location.state!.typeDemande)
		}))
			.then(() => redirectionVersAcquisitionResidence(intl.locale))
			.catch(
				(error) => {
					setPostingData(false)
					dispatch(
						errorCatcher(error, POST_CHANGEMENT_APPARTEMENT)
					)
				}
			)
	}

	return (
		<Grid container item xs={12}>
			<Grid container item xs={12}>
				<h1 className={classes.titleStyle}>
					<FormattedMessage
						id="changementAppartement.formulaire.titre"
						defaultMessage="Votre nouveau besoin"
						description="Titre du formulaire de changement d'appartement"
					/>
				</h1>
			</Grid>

			<Form
				mutators={{
					...arrayMutators
				}}
				initialValues={formValeursInitiales}
				validate={validate}
				onSubmit={onSubmit}
				render={
					({ handleSubmit, form, values }) => (
						<form onSubmit={handleSubmit} style={{ width: '100%' }}>
							{/* Champ sur le choix de rester dans la même résidence */}
							<Grid container item xs={12}>
								<Field
									name={formulaireChangementAppartementEnum.choixResidence}
									component={FormFabField}
									form={form}
									label={intl.formatMessage({
										id: 'changementAppartement.formulaire.choixResidence.label',
										defaultMessage: 'Souhaitez-vous rester dans la même résidence ?',
										description: 'Label du champ sur le choix de la résidence'
									})}
									elements={
										objectGetKeys(choixResidenceEnum)
											.filter((choixResidence) => isNaN(Number(choixResidence)))
									}
									enumToParse={choixResidenceEnum}
								/>
							</Grid>

							{/* Champ permettant de sélectionner les villes / arrondissement souhaités, affiché uniquement si le choix de changement est non */}
							{
								values[formulaireChangementAppartementEnum.choixResidence] === choixResidenceEnum.non && (
									<Grid container item xs={12} className={classes.conteneurChamp}>
										<FieldArray
											name={formulaireChangementAppartementEnum.ville}
											render={
												({ fields, meta }) => (
													<SelecteurVille
														fields={fields}
														meta={meta}
														label={
															intl.formatMessage({
																id: 'changementAppartement.formulaire.ville.label',
																defaultMessage: 'Villes et arrondissement souhaités',
																description: 'Label des villes / arrondissement souhaités'
															})
														}
													>
														<MenuItem value="0" disabled>
															<FormattedMessage
																id="changementAppartement.formulaire.ville.placeholder"
																defaultMessage="Choisissez parmi la liste"
																description="Placeholder du champ villes et arrondissement souhaités"
															/>
														</MenuItem>

														{
															objectGetKeys(villeEnum)
																.filter(
																	(ville) => !values[formulaireChangementAppartementEnum.ville].includes(villeEnum[ville as keyof typeof villeEnum]) && isNaN(Number(ville))
																)
																.map(
																	(ville) => (
																		<MenuItem
																			key={ville}
																			value={villeEnum[ville as keyof typeof villeEnum]}
																		>
																			<FormattedMessage
																				id={`changementAppartement.formulaire.ville.${ville}`}
																				defaultMessage="Paris (75)"
																				description="Valeur du champ villes et arrondissement souhaités"
																			/>
																		</MenuItem>
																	)
																)
														}
													</SelecteurVille>
												)
											}

										/>
									</Grid>
								)
							}

							{
								concerneAppartement() ? (
									// Champs affichés uniquement si l'utilisateur a choisi de changer/d'ajouter un appartement
									<>
										{/*	Champ pour le nombre de pièces */}
										<Grid container item xs={12} className={classes.conteneurChamp}>
											<Field
												name={formulaireChangementAppartementEnum.nombrePieces}
												component={FormFabField}
												form={form}
												label={
													`${intl.formatMessage({
														id: 'changementAppartement.formulaire.nombrePieces.label',
														defaultMessage: 'Nombre de pièces',
														description: 'Label du champ sur le choix du nombre de pièces'
													})} *`}
												elements={
													objectGetKeys(nombrePiecesEnum)
														.filter((nombrePiece) => isNaN(Number(nombrePiece)))
												}
												enumToParse={nombrePiecesEnum}
												multipleValue
											/>
										</Grid>

										{/*	Champ pour le nombre de chambres */}
										<Grid container item xs={12} className={classes.conteneurChamp}>
											<Field
												name={formulaireChangementAppartementEnum.nombreChambres}
												component={FormFabField}
												form={form}
												label={
													`${intl.formatMessage({
														id: 'changementAppartement.formulaire.nombreChambres.label',
														defaultMessage: 'Nombre de chambres',
														description: 'Label du champ sur le choix du nombre de chambres'
													})} *`}
												elements={
													objectGetKeys(nombreChambreEnum)
														.filter((nombreChambre) => isNaN(Number(nombreChambre)))
												}
												enumToParse={nombreChambreEnum}
											/>
										</Grid>

										{/*	Champ pour les espaces extérieurs */}
										<Grid container item xs={12} className={classes.conteneurChamp}>
											<Field
												name={formulaireChangementAppartementEnum.espaceExterieur}
												component={FormFabField}
												form={form}
												label={intl.formatMessage({
													id: 'changementAppartement.formulaire.espaceExterieur.label',
													defaultMessage: 'Espace extérieur',
													description: 'Label du champ sur le choix de l\'espace extérieur'
												})}
												elements={
													objectGetKeys(espaceExterieurEnum)
														.filter((espaceExterieur) => isNaN(Number(espaceExterieur)))
												}
												enumToParse={espaceExterieurEnum}
												multipleValue
											/>
										</Grid>
									</>
								) : (
									// Champs affichés uniquement si l'utilisateur a choisi de changer/d'ajouter un parking
									<>
										{/*	Champ pour le type de véhicule */}
										<Grid container item xs={12} className={classes.conteneurChamp}>
											<Field
												name={formulaireChangementAppartementEnum.typeVehicule}
												component={FormFabField}
												form={form}
												label={`${intl.formatMessage({
													id: 'changementAppartement.formulaire.typeVehicule.label',
													defaultMessage: 'Pour quel type de véhicules ?',
													description: 'Label du champ sur le choix du type de véhicule'
												})} *`}
												elements={
													objectGetKeys(typeVehiculeEnum)
														.filter((typeVehicule) => isNaN(Number(typeVehicule)))
												}
												enumToParse={typeVehiculeEnum}
											/>
										</Grid>

										{/*	Champs affiché uniquement si le type de véhicule est voiture */}
										{
											values[formulaireChangementAppartementEnum.typeVehicule] === typeVehiculeEnum.voiture && (
												<DimensionVehicule />
											)
										}
									</>
								)
							}

							<Grid container item xs={12} className={classes.conteneurChamp}>
								{
									concerneAppartement() && (
										// 	Champ pour le budget maximum
										<Grid container alignItems="center" item xs={7} md={4} style={{ position: 'relative' }}>
											<Field
												name={formulaireChangementAppartementEnum.budgetMaximum}
												component={FormNumberFormatField}
												label={`${intl.formatMessage({
													id: 'changementAppartement.formulaire.budgetMaximum.label',
													defaultMessage: 'Budget maximum',
													description: 'Label du champ sur le budget maximum'
												})} *`}
												allowNegative={false}
												decimalScale={0}
												maxLength={4}
												formType={formTypeEnum.textFieldInline}
												suffixComponent={
													<>
														<span className={classes.devise}>
															<FormattedMessage
																id="app.devise"
																defaultMessage="euros"
																description="Devise du budget"
															/>
														</span>

														<Tooltip
															title={intl.formatMessage({
																id: 'changementAppartement.formulaire.budgetMaximum.tooltip',
																defaultMessage: 'Votre revenu doit être égal à 3 fois le montant du loyer charges comprises',
																description: 'Texte du tooltip du budget maximum'
															})}
															open={open}
															setOpen={setOpen}
															tooltipContainerStyle={classes.tooltipContainerStyle}
														>
															{
																open ? (
																	<InfoIcon />
																) : (
																	<InfoOutlinedIcon />
																)
															}
														</Tooltip>
													</>
												}
											/>
										</Grid>
									)
								}

								{/*	Champ pour la date d'arrivée */}
								<Grid container item xs={12} md={6}>
									<Grid container item xs={9} md={6} alignItems="center" className={classes.labelStyle}>
										<FormattedMessage
											id="changementAppartement.formulaire.dateArrivee.label"
											defaultMessage="Date d'arrivée souhaitée"
											description="Label pour le champ de la date d'arrivée"
										/> *

										<Tooltip
											title={intl.formatMessage({
												id: 'changementAppartement.formulaire.dateArrivee.tooltip',
												defaultMessage: 'Nous vous confirmerons votre date d\'arrivée dès la validation de votre dossier',
												description: 'Texte du tooltip de la date d\'arrivée'
											})}
											open={openDateArrivee}
											setOpen={setOpenDateArrivee}
											tooltipContainerStyle={classes.tooltipContainerStyle}
										>
											{
												openDateArrivee ? (
													<InfoIcon />
												) : (
													<InfoOutlinedIcon />
												)
											}
										</Tooltip>
									</Grid>

									<Grid item xs={12} className={classes.datepicker}>
										<Field
											initialValue={getSpecificDate(0)}
											name={formulaireChangementAppartementEnum.dateArrivee}
											type="date"
											component={FormDatePickerAcquiResiField}
											inputTextStyle={dateText}
											isGreyBackground={false}
											setMinDate={getSpecificDate(0)}
											disableToolbar={true}
										/>
									</Grid>
								</Grid>
							</Grid>

							{/*	Champ pour le commentaire */}
							<Grid container item xs={12} className={classes.conteneurChamp}>
								<Field
									name={formulaireChangementAppartementEnum.commentaire}
									component={FormTextFieldInline}
									label={intl.formatMessage({
										id: 'changementAppartement.formulaire.commentaire.label',
										defaultMessage: 'Commentaire',
										description: 'Label du champ sur commentaire'
									})}
									maxLength={1500}
									multiline
								/>
							</Grid>

							{
								concerneAppartement() && (
									// Champ pour le motif de la demande
									<Grid container item xs={12} className={classes.conteneurChamp}>
										<Field
											name={formulaireChangementAppartementEnum.raisonRecherche}
											component={RaisonRecherche}
										/>
									</Grid>
								)
							}

							{/*	Champ pour la sélection du profil locataire */}
							{/* On affiche le bloc de sélection de type locataire uniquement pour un utilisateur de type contact */}
							<Grid container item xs={12} className={classes.conteneurChamp}>
								{
									userInfos.type === typeProspectEnum.contact && (
										<Field
											name={formulaireChangementAppartementEnum.profilLocataire}
											component={ProfilLocataire}
											form={form}
											concerneAppartement={concerneAppartement()}
										/>
									)
								}

								{
									values[formulaireChangementAppartementEnum.profilLocataire] === profilLocataireCRMEnum.family && (
										<Grid container>
											<Field
												name={formulaireChangementAppartementEnum.familleMonoparental}
												component={CheckBoxField}
												type="checkbox"
												label={
													intl.formatMessage(
														{
															id: 'changementAppartement.formulaire.familleMonoparental.label',
															defaultMessage: 'Vous êtes une famille monoparentale (un seul adulte signataire du bail)',
															description: 'Monoparental family checkbox'
														}
													)
												}
												textStyle="text4"
											/>
										</Grid>
									)
								}

								{
									values[formulaireChangementAppartementEnum.profilLocataire] === profilLocataireCRMEnum.other && (
										<Grid container>
											<Grid item xs={12} md={6}>
												<Field
													name={formulaireChangementAppartementEnum.nbColoc}
													component={FormNumberWithButtonsField}
													id={formulaireChangementAppartementEnum.nbColoc}
													label={
														intl.formatMessage(
															{
																id: 'changementAppartement.formulaire.nbColoc.label',
																defaultMessage: 'Nombre de colocataires (vous compris)',
																description: 'Nb coloc field'
															}
														)
													}
													min={2}
													max={3}
												/>
											</Grid>
										</Grid>
									)
								}
							</Grid>

							{/* Bloc sur les copreneurs */}
							{/* Afficher si l'utilisateur à un profil locataire différent de seul et qu'il n'est pas en famille monoparentale */}
							{/* Et s'il possède un contrat en exploitation avec des colocataires */}
							{
								values[formulaireChangementAppartementEnum.profilLocataire]
								&& values[formulaireChangementAppartementEnum.profilLocataire] !== profilLocataireCRMEnum.alone
								&& !values[formulaireChangementAppartementEnum.familleMonoparental]
								&& contratExploitationEtColocataire().length > 0
								&& (
									<Grid container item xs={12} className={classes.conteneurChamp}>
										<FieldArray
											name={formulaireChangementAppartementEnum.selectionCopreneur}
											render={
												({ fields, meta }) => (
													<SelecteurCopreneur
														fields={fields}
														meta={meta}
														label={intl.formatMessage({
															id: `changementAppartement.formulaire.selectionCopreneur.label.${concerneAppartement() ? 'appartement' : 'parking'}`,
															defaultMessage: 'Qui vous suivra dans le futur appartement ?',
															description: 'Label du champ de selection des copreneurs'
														})}
														copreneurs={contratExploitationEtColocataire()[0].interlocuteursRoleBail.filter((interlocuteur) => interlocuteur.user_id !== userInfos.id)}
														selectionMax={values[formulaireChangementAppartementEnum.profilLocataire] === profilLocataireCRMEnum.other ? values[formulaireChangementAppartementEnum.nbColoc] : 1}
													/>
												)
											}
										/>
									</Grid>
								)
							}

							{/* Bloc étudiant pour profil "seul" */}
							{
								values[formulaireChangementAppartementEnum.profilLocataire]
								&& values[formulaireChangementAppartementEnum.profilLocataire] === profilLocataireCRMEnum.alone
								&& (
									<Grid container item direction="column" xs={12} className={classes.conteneurChamp}>
										<span className={classes.title6}>
											<FormattedMessage
												id={`changementAppartement.formulaire.${formulaireChangementAppartementEnum.isStudent}.label`}
												defaultMessage="Êtes-vous étudiant ?"
												description="is student ?"
											/>
										</span>
										{!concerneAjoutParking() &&
											<span className={classes.text2}>
												<FormattedMessage
													id={`changementAppartement.formulaire.${formulaireChangementAppartementEnum.isStudent}.subTitle`}
													defaultMessage="Si « Oui » et <span>{mainInformation}</span>, il sera nécessaire d'identifier un garant pour réserver le bien choisi."
													description="is student description"
													values={
														{
															span: (chunk: string) => <span className={classes.greenText}>{chunk}</span>,
															mainInformation: intl.formatMessage(
																{
																	id: `changementAppartement.formulaire.${formulaireChangementAppartementEnum.isStudent}.mainInformation`,
																	defaultMessage: 'en l\'absence d\'une bourse',
																	description: 'disclaimer boursier'
																}
															)
														}
													}
												/>
											</span>
										}
										<Field
											name={formulaireChangementAppartementEnum.isStudent}
											component={FormFabField}
											form={form}
											elements={
												objectGetKeys(ouiNonEnum)
													.filter((ouiNonEnum) => isNaN(Number(ouiNonEnum)))
											}
											enumToParse={ouiNonEnum}
										/>
									</Grid>
								)
							}

							{/* Bloc boursier pour profil "seul" && étudiant */}
							{
								!concerneAjoutParking()
								&& values[formulaireChangementAppartementEnum.profilLocataire]
								&& values[formulaireChangementAppartementEnum.profilLocataire] === profilLocataireCRMEnum.alone
								&& values[formulaireChangementAppartementEnum.isStudent] === ouiNonEnum.oui
								&& (
									<Grid container item xs={12} className={classes.conteneurChamp}>
										<span className={classes.title6}>
											<FormattedMessage
												id={`changementAppartement.formulaire.${formulaireChangementAppartementEnum.isBoursier}.label`}
												defaultMessage="Êtes-vous boursier ?"
												description="is boursier ?"
											/>
										</span>
										<Field
											name={formulaireChangementAppartementEnum.isBoursier}
											component={FormFabField}
											form={form}
											elements={
												objectGetKeys(ouiNonEnum)
													.filter((ouiNonEnum) => isNaN(Number(ouiNonEnum)))
											}
											enumToParse={ouiNonEnum}
										/>
									</Grid>
								)
							}
							<PromoCode name={formulaireChangementAppartementEnum.codePrivilege} />
							<Grid
								container
								justify="space-between"
								alignItems="center"
								className={buttonContainerClass}
							>
								<Grid container item xs={12} md={3} className={buttonWidthClass} justify="flex-start">
									<Button
										color="colorBlue"
										background="backgroundWhite"
										textStyle="text8"
										onClick={() => history.push(linksEnum.requests)}
										buttonStyle={classes.buttonLongText}
										fullWidth
									>
										<ChevronLeftIcon className={classes.chevronLeftIcon} />

										<FormattedMessage
											id="global.back"
											defaultMessage="Retour"
											description="Back link text"
										/>
									</Button>
								</Grid>

								<Grid item xs={12} md={4} className={buttonWidthClass}>
									{
										postingData ? (
											<Loader size={40} />
										) : (
											<Button
												background="backgroundAccessibility"
												color="colorWhite"
												textStyle="text8"
												type="submit"
												buttonStyle={buttonClass}
											>
												<FormattedMessage
													id="global.btn.continue"
													defaultMessage="Continuer"
													description="Next button"
												/>

												<ChevronRightIcon className={classes.chevronRightStyle} />
											</Button>
										)
									}
								</Grid>
							</Grid>
						</form>
					)
				}
			/>
		</Grid>
	)
}

export default FormulaireChangementAppartement
